/** @format */

import { useEffect } from "react";
import { IoClose } from "react-icons/io5";
import { MdOutlineArrowBackIos, MdOutlineArrowForwardIos } from "react-icons/md";
import I18n from "../../pages/private/languageSelector";

const Drawer = ({
  isOpen,
  customClasses,
  onClose,
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  buttonTitle1 = <I18n lng="global.Close" />,
  buttonTitle2,
  children,
  primaryActionButton,
  secondaryActionButton,
  primaryButtonClasses = "",
  secondaryButtonClasses = "",
  childClasses,
  titleClasses,
  buttonClasses,
  isBg,
  secondaryDisabled,
  primaryDisabled,
  isHomePageSideBar = false,
  width = "sm:w-[420px]",
}) => {
  const language = localStorage.getItem("language");
  const isRTL = language === "ARABIC";

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        onClose?.();
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  const getDrawerPosition = () => {
    if (isHomePageSideBar) {
      return isRTL ? "right-0" : "left-0";
    }
    return isRTL ? "left-0" : "right-0";
  };

  const getTransform = () => {
    if (!isOpen) {
      if (isHomePageSideBar) {
        return isRTL ? "translate-x-full" : "-translate-x-full";
      }
      return isRTL ? "-translate-x-full" : "translate-x-full";
    }
    return "translate-x-0";
  };

  return (
    <div
      className={`fixed inset-0 z-50 ${isOpen ? "visible" : "invisible"}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="drawer-title"
    >
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0"
        }`}
        onClick={secondaryAction || onClose}
        aria-hidden="true"
      />

      {/* Drawer Panel */}
      <div
        className={`absolute ${getDrawerPosition()} h-full ${width} w-full 
          ${isBg ? "bg-[#FAFAFA]" : "bg-white"} 
          shadow-2xl flex flex-col
          transform transition-transform duration-300 ease-out ${getTransform()}`}
        style={{ direction: isRTL ? "rtl" : "ltr" }}
      >
        {/* ===== HEADER ===== */}
        {buttonTitle1 === "Response" ? (
          /* Response-style header with back arrow */
          <div className="flex items-center justify-center relative px-4 py-4 border-b border-gray-100 bg-white">
            <button
              onClick={onClose}
              className={`absolute ${isRTL ? "right-4" : "left-4"} p-2 rounded-lg text-colPrimary hover:bg-colPrimary/10 transition-colors`}
              aria-label="Go back"
            >
              {isRTL ? <MdOutlineArrowForwardIos size={18} /> : <MdOutlineArrowBackIos size={18} />}
            </button>
            <h2 id="drawer-title" className={`text-base font-semibold text-[#333333] ${titleClasses}`}>
              {title}
            </h2>
          </div>
        ) : (
          /* Standard header with close/action buttons */
          <div className={`flex-shrink-0 bg-white border-b border-gray-100 ${buttonClasses}`}>
            <div className="flex items-center justify-between px-5 py-4 min-h-[64px]">
              {/* Close Button — renders the default IoClose + optional text label.
                  If the caller passes a custom React element (e.g. <img> close
                  icon), render that ALONE so we don't double up close affordances.
                  Bug fix 2026-05-08: NavigationModa was showing two X icons on
                  sm: (≥640px) breakpoint because buttonTitle1=<img> was rendered
                  alongside the default <IoClose>. */}
              <button
                onClick={onClose}
                className={`inline-flex items-center gap-1.5 text-sm font-medium text-quaternary
                  hover:text-[#333333] transition-colors px-2 py-1.5 -ml-2 rounded-lg hover:bg-gray-100 flex-shrink-0
                  ${primaryButtonClasses}`}
                aria-label="Close"
              >
                {buttonTitle1 && typeof buttonTitle1 !== 'string' && buttonTitle1?.type !== I18n ? (
                  buttonTitle1
                ) : (
                  <>
                    <IoClose size={18} />
                    <span className="hidden sm:inline">{buttonTitle1}</span>
                  </>
                )}
              </button>

              {/* Title - Centered */}
              <div className="flex-1 flex justify-center px-2">
                <h2
                  id="drawer-title"
                  className={`text-[15px] font-semibold text-[#333333] whitespace-nowrap ${titleClasses}`}
                >
                  {title}
                </h2>
              </div>

              {/* Primary Action Button */}
              {buttonTitle2 && (
                <button
                  onClick={() => primaryAction?.()}
                  disabled={primaryDisabled}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold 
                    rounded-lg transition-all duration-200
                    ${
                      primaryDisabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-colPrimary text-white hover:bg-[#2488c2] shadow-sm hover:shadow-md"
                    }
                    ${secondaryButtonClasses}`}
                >
                  {buttonTitle2}
                </button>
              )}
            </div>

            {/* Optional Subtitle */}
            {subtitle && (
              <p className="px-5 pb-3 text-sm text-quaternary -mt-1">{subtitle}</p>
            )}
          </div>
        )}

        {/* ===== CONTENT ===== */}
        <div
          className={`flex-1 overflow-y-auto overscroll-contain
            ${customClasses === "dispatchDrawer" ? "p-3" : "p-5"} 
            ${childClasses}`}
        >
          {children}
        </div>

        {/* ===== FOOTER (Optional) ===== */}
        {(secondaryActionButton || primaryActionButton) && (
          <div className="flex-shrink-0 border-t border-gray-200 bg-white px-5 py-4">
            <div className="flex items-center justify-end gap-3">
              {secondaryActionButton && (
                <button
                  onClick={secondaryAction}
                  disabled={secondaryDisabled}
                  className={`px-4 py-2.5 text-sm font-medium text-[#333333] 
                    bg-white border border-gray-300 rounded-lg
                    hover:bg-gray-50 hover:border-gray-400 
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-200`}
                >
                  {secondaryActionButton}
                </button>
              )}
              {primaryActionButton && (
                <button
                  onClick={primaryAction}
                  disabled={primaryDisabled}
                  className={`px-5 py-2.5 text-sm font-semibold text-white 
                    bg-colPrimary rounded-lg shadow-sm
                    hover:bg-[#2488c2] hover:shadow-md
                    disabled:bg-colPrimaryDisalbed disabled:cursor-not-allowed
                    transition-all duration-200
                    ${primaryButtonClasses}`}
                >
                  {primaryActionButton}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Drawer;
